import type { Filter } from '../components/carsFilter';
import type { ColorKey } from '../components/colorTag';

// Existing filter links contain a second URI-encoding layer. Keep them valid.
export function decodeCarListParam(value: string): string {
  try { return decodeURIComponent(value); } catch { return value; }
}

export function readCarListColors(value: string): ColorKey[] {
  try {
    const colors: unknown = JSON.parse(decodeCarListParam(value));
    return Array.isArray(colors) ? colors.filter((color): color is ColorKey => typeof color === 'string') : [];
  } catch { return []; }
}

export function readCarListFilter(search: string): Filter {
  const params = new URLSearchParams(search);
  const text = (key: string) => params.has(key) ? decodeCarListParam(params.get(key)!) : undefined;
  const number = (key: string) => {
    const value = params.get(key);
    return value && Number.isFinite(Number(value)) ? Number(value) : undefined;
  };
  return {
    make: text('make'), model: text('model'), category: text('category'),
    // The search function decodes this existing parameter itself.
    search: params.get('search') || undefined,
    priceFrom: number('priceFrom'), priceTo: number('priceTo'),
    mileageFrom: number('mileageFrom'), mileageTo: number('mileageTo'),
    powerFrom: number('powerFrom'), powerTo: number('powerTo'),
    firstRegistrationFrom: number('firstRegistrationFrom'), firstRegistrationTo: number('firstRegistrationTo'),
    colors: readCarListColors(params.get('exteriorColors') || '')
  };
}

export function restoreCarListScroll(browser: Window, position: [number, number]): void {
  const { pathname, search } = browser.location;
  let remainingFrames = 300;
  let readyLastFrame = false;
  const restore = () => {
    if (browser.location.pathname !== pathname || browser.location.search !== search) return;
    const list = browser.document.querySelector<HTMLElement>('[data-car-list-ready="true"]');
    const ready = !!list && list.dataset.carListSearch === search;
    // Wait for React's filter, pagination and card effects before restoring.
    if (ready && readyLastFrame) {
      browser.scrollTo(position[0], position[1]);
    } else if (--remainingFrames > 0) {
      readyLastFrame = ready;
      browser.requestAnimationFrame(restore);
    }
  };
  browser.requestAnimationFrame(restore);
}

type ListLocation = { pathname: string; key?: string; state?: object | null };
let initialEntryId = '';

export function initializeCarListHistory(browser: Window): void {
  // Gatsby restores every SPA route; native restoration can run against the outgoing DOM.
  browser.history.scrollRestoration = 'manual';
  initialEntryId = browser.history.state?.acmListEntryId || browser.crypto.randomUUID();
  browser.history.replaceState({ ...browser.history.state, acmListEntryId: initialEntryId }, '');
}
export const isCarListPath = (pathname: string): boolean => /^\/(?:en\/|fr\/)?cars\/?$/.test(pathname);
const scrollKey = (location: ListLocation) => {
  // Reach calls each full-document entry 'initial'; give those entries a stable native identity.
  const entryId = location.key && location.key !== 'initial' ? location.key
    : (location.state as { acmListEntryId?: string } | null)?.acmListEntryId || initialEntryId;
  return `acm:car-list-scroll:${location.pathname}:${entryId}`;
};
const scrollPositions = new Map<string, [number, number]>();

export function saveCarListScroll(browser: Window, location: ListLocation): void {
  if (!isCarListPath(location.pathname)) return;
  const position: [number, number] = [browser.scrollX, browser.scrollY];
  const key = scrollKey(location);
  scrollPositions.set(key, position);
  try { browser.sessionStorage.setItem(key, JSON.stringify(position)); } catch { /* Private storage may be unavailable. */ }
}

export function readCarListScroll(browser: Window, location: ListLocation): [number, number] | undefined {
  const key = scrollKey(location);
  const memory = scrollPositions.get(key);
  if (memory) return memory;
  try {
    const position = JSON.parse(browser.sessionStorage.getItem(key) || 'null');
    if (Array.isArray(position) && position.length === 2 && position.every(n => typeof n === 'number' && Number.isFinite(n))) return [position[0], position[1]];
  } catch { /* Use Gatsby's saved position when storage is unavailable. */ }
}
